// Copyright text placeholder, Warner Bros. Discovery, Inc.

/**
 *  WARNING:
 *  This file is auto-generated. Do not edit manually. Any changes made to this
 *  file will be overwritten the next time it is generated. If you need to make
 *  changes, please modify the source files and re-generate this file.
 */

import { BaseError, type IVersionedEvent } from '../../../core';

/**
 * http method used to make the request
 * @public
 * @readonly
 */

enum MethodV1 {
  /** Method of GET **/
  GET = 'GET',
  /** Method of POST **/
  POST = 'POST',
  /** Method of PUT **/
  PUT = 'PUT',
  /** Method of DELETE **/
  DELETE = 'DELETE',
  /** Method of PATCH **/
  PATCH = 'PATCH'
}

/**
 *
 *
 * version 1.0
 * @public
 */
interface IErrV1 {
  /** When set, signifies that this event represents an error, and indicates the high-level category and scope of the error.
     USER_FACING errors represent any situation where the user was directly impacted by the error. It may manifest as an error popup, application crash, missing content or any action critical to the user journey.
     RECOVERABLE errors represent any error that degrades user experience. A subset of the action was performed but failed. The user can still continue on their journey.
     BACKGROUND errors represent any error that occurs but there is no impact to the user. The error is important to capture and watch for anomalies but does not raise immediate operational concerns. **/
  scope?: string;
}

/**
 *
 *
 * version 1.0
 * @public
 */
interface IOmdV1 {
  /** Uniquely identifiable name for each sdk and application. One Jira team may be responsible for multiple components. Other OMD information can be inferred from this value. **/
  component?: string;
}

/**
 *
 *
 * version 1.0
 * @public
 */
interface IRequestV1 {
  /** http method used to make the request **/
  method: MethodV1;
  /** How many retries have been initiated for this request. **/
  retryCount: number;
  /** unique identifier for the request **/
  traceId: string;
  /** Uniform Resource Locator (URL) that specifies the location of a resource on the internet and the method for retrieving it. The request URL should include the protocol (such as HTTP or HTTPS), the domain name, the path to the specific resource on the server, and any query parameters or other information that is needed to retrieve the resource. **/
  url: string;
}

/**
 *
 *
 * version 1.0
 * @public
 */
interface IErrorCodesV1 {
  /** service error code provided by the response payload **/
  code: string;
  /** Unique identifier from service for the matching code **/
  id: string;
}

/**
 *
 *
 * version 1.0
 * @public
 */
interface IResponseV1 {
  /** A numerical value that is returned by a server in response to a client&#x27;s request. Restricted to the 4xx and 5xx ranges. **/
  statusCode: number;
  /**  **/
  errorCodes?: IErrorCodesV1[];
}

/**
 *
 *
 * version 1.0
 * @public
 */
interface IHttpV1Payload {
  /**  **/
  err: IErrV1;
  /**  **/
  request: IRequestV1;
  /**  **/
  response: IResponseV1;
  /**  **/
  omd?: IOmdV1;
}

/**
 * properties for ServiceHttpV1 error
 * @public
 */
export interface IServiceHttpV1Props extends IHttpV1Payload {}

/**
 * IHttpV1 error
 * An event emitted when a request has returned a status code within 4xx and 5xx ranges.
 * @public
 */
export class ServiceHttpV1 extends BaseError<IServiceHttpV1Props, IHttpV1Payload> {
  /** unique event type **/
  public readonly eventType: string = 'beam.errors.service.http_1.0';
  /** http method used to make the request **/
  public static METHOD: typeof MethodV1 = MethodV1;

  /**
   * overrides the default _build method to add default values for fields
   * @param props - IErrV1
   * @returns IErrV1
   */
  private _buildErrV1(props: IErrV1): IErrV1 {
    return {
      ...props,
      scope: props.scope ?? 'BACKGROUND'
    };
  }
  /**
   * overrides the default _build method to add default values for fields
   * @param props - IOmdV1
   * @returns IOmdV1
   */
  private _buildOmdV1(props: IOmdV1): IOmdV1 {
    return props;
  }
  /**
   * overrides the default _build method to add default values for fields
   * @param props - IRequestV1
   * @returns IRequestV1
   */
  private _buildRequestV1(props: IRequestV1): IRequestV1 {
    return props;
  }
  /**
   * overrides the default _build method to add default values for fields
   * @param props - IErrorCodesV1
   * @returns IErrorCodesV1
   */
  private _buildErrorCodesV1(props: IErrorCodesV1): IErrorCodesV1 {
    return props;
  }
  /**
   * overrides the default _build method to add default values for fields
   * @param props - IResponseV1
   * @returns IResponseV1
   */
  private _buildResponseV1(props: IResponseV1): IResponseV1 {
    return props;
  }

  /**
   * @param payload - The payload for the error
   * @returns IVersionedEvent<IServiceHttpV1Props>
   */
  protected override _build(props: IServiceHttpV1Props): IVersionedEvent<IHttpV1Payload> {
    const payload: IHttpV1Payload = {
      ...props,
      err: this._buildErrV1(props.err as IErrV1),
      request: this._buildRequestV1(props.request as IRequestV1),
      response: this._buildResponseV1(props.response as IResponseV1),
      omd: this._buildOmdV1(props.omd as IOmdV1)
    };

    return super._build(payload);
  }
}

export { MethodV1 as ServiceHttpV1Method };
export type { IErrV1 as IServiceHttpV1Err };
export type { IOmdV1 as IServiceHttpV1Omd };
export type { IRequestV1 as IServiceHttpV1Request };
export type { IErrorCodesV1 as IServiceHttpV1ErrorCodes };
export type { IResponseV1 as IServiceHttpV1Response };
export type { IHttpV1Payload as IServiceHttpV1Payload };
